import React from 'react'
import { TouchableWithoutFeedback, View } from 'react-native'

import { B4, B5, Stack, XStack } from '@zet/design-system'

import CompletedTick from '../../../assets/images/icons/completedTick.svg'

interface IProps {
  currentPosition: number
  stepCount: number
  labels: Array<string>
  onPress: (e: number) => void
}

const STEP_STATUS = {
  CURRENT: 'current',
  FINISHED: 'finished',
  UNFINISHED: 'unfinished',
}
const defaultStyles = {
  stepIndicatorSize: 30,
  currentStepIndicatorSize: 30,
  separatorStrokeWidth: 3,
  separatorStrokeUnfinishedWidth: 3,
  separatorStrokeFinishedWidth: 0,
  currentStepStrokeWidth: 3,
  stepStrokeWidth: 3,
  stepStrokeCurrentColor: '#389CFF',
  stepStrokeFinishedColor: 'green',
  stepStrokeUnFinishedColor: '#7E89A1',
  separatorFinishedColor: '#7E89A1',
  separatorUnFinishedColor: '#7E89A1',
  stepIndicatorFinishedColor: 'green',
  stepIndicatorUnFinishedColor: '#ffffff',
  stepIndicatorCurrentColor: '#ffffff',
  stepIndicatorLabelCurrentColor: '#000000',
  stepIndicatorLabelFinishedColor: '#ffffff',
  stepIndicatorLabelUnFinishedColor: 'green',
}

const StepIndicator: React.FC<IProps> = ({
  currentPosition = 0,
  stepCount = 6,
  labels = [],
  onPress,
}) => {
  const stepPressed = (position: number) => {
    if (onPress) {
      onPress(position)
    }
  }

  const renderStepIndicator = () => {
    const steps = []
    for (let position = 0; position < stepCount; position++) {
      steps.push(
        <TouchableWithoutFeedback
          key={position}
          onPress={() => stepPressed(position)}
        >
          <View className="items-center">
            {renderStep(position)}
            <View
              className={`${
                position + 1 === stepCount
                  ? 'bg-white'
                  : position <= currentPosition - 1
                  ? 'bg-[#95D68F]'
                  : 'bg-grays-40'
              } my-2  h-[40px] w-[2px]`}
            />
          </View>
        </TouchableWithoutFeedback>
      )
    }
    return <View>{steps}</View>
  }

  const renderStepLabels = () => {
    if (!labels || labels.length === 0) {
      return
    }
    const labelViews = labels.map((label, index) => {
      return (
        <TouchableWithoutFeedback
          key={index}
          onPress={() => stepPressed(index)}
        >
          <View
            className={`mx-2 mb-2 max-h-9 flex-1 justify-around rounded-lg border-2 ${
              currentPosition === index
                ? 'border-[#5FAFFF]'
                : currentPosition > index
                ? 'border-[#ECEFF5]'
                : 'border-[#ECEFF5]'
            }
              ${
                currentPosition === index
                  ? 'bg-[#EBF3FF]'
                  : currentPosition > index
                  ? 'bg-white'
                  : 'bg-[#F5F7FB]'
              } p-2`}
          >
            <B5 className="text-grays-70 pb-1">Step {index + 1}</B5>
            <B4
              className={`text-sm ${
                currentPosition === index ? 'text-[#103976]' : 'text-[#0C0E11]'
              }`}
            >
              {label}
            </B4>
          </View>
        </TouchableWithoutFeedback>
      )
    })

    return <View className="ml-2 w-11/12">{labelViews}</View>
  }

  const renderStep = (position: number) => {
    let stepStyle
    switch (getStepStatus(position)) {
      case STEP_STATUS.CURRENT: {
        stepStyle = {
          backgroundColor: defaultStyles.stepIndicatorCurrentColor,
          borderWidth: defaultStyles.currentStepStrokeWidth,
          borderColor: defaultStyles.stepStrokeCurrentColor,
          height: 20,
          width: 20,
          borderRadius: defaultStyles.stepIndicatorSize,
          overflow: 'hidden',
        }

        break
      }
      case STEP_STATUS.FINISHED: {
        stepStyle = {
          backgroundColor: defaultStyles.stepIndicatorFinishedColor,
          borderWidth: defaultStyles.stepStrokeWidth,
          borderColor: defaultStyles.stepStrokeFinishedColor,
          height: 20,
          width: 20,
          borderRadius: defaultStyles.stepIndicatorSize / 2,
          overflow: 'hidden',
        }
        break
      }

      case STEP_STATUS.UNFINISHED: {
        stepStyle = {
          backgroundColor: defaultStyles.stepIndicatorUnFinishedColor,
          borderWidth: defaultStyles.stepStrokeWidth,
          borderColor: defaultStyles.stepStrokeUnFinishedColor,
          height: 20,
          width: 20,
          borderRadius: defaultStyles.stepIndicatorSize / 2,
          overflow: 'hidden',
        }
        break
      }
      default:
    }

    return (
      <>
        {position < currentPosition ? (
          <CompletedTick />
        ) : (
          <View key={'step-indicator'} style={{ ...stepStyle }}></View>
        )}
      </>
    )
  }

  const getStepStatus = (stepPosition: number) => {
    if (stepPosition === currentPosition) {
      return STEP_STATUS.CURRENT
    } else if (stepPosition < currentPosition) {
      return STEP_STATUS.FINISHED
    } else {
      return STEP_STATUS.UNFINISHED
    }
  }

  return (
    <XStack>
      <Stack className="mt-2 flex-none">{renderStepIndicator()}</Stack>
      {labels && renderStepLabels()}
    </XStack>
  )
}

export default React.memo(StepIndicator)
